import {type Active, type Over} from '@dnd-kit/core';
import {useCallback} from 'react';
import {type VirtuosoHandle} from 'react-virtuoso';

import {useViewAction} from '../../../../state/views/hooks';
import * as Actions from '../../../../state/views/panelBankConfig/actions';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {type PanelDropRef, type SectionWithRef} from '../../types';
import {getDndSortableSectionId} from '../utils';

export const useHandleSectionDragEnd = ({
  visibleSections,
  virtuosoRef,
}: {
  visibleSections: SectionWithRef[];
  virtuosoRef: React.RefObject<VirtuosoHandle>;
}) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const moveSection = useViewAction(panelBankConfigRef, Actions.moveSection);

  const handleSectionDragEnd = useCallback(
    (active: Active | null, over: Over | null) => {
      const activeSectionRefId = active?.id.toString();
      const targetSectionRefId = over?.id.toString();

      if (!activeSectionRefId || !targetSectionRefId) {
        return;
      }

      moveSection(activeSectionRefId, targetSectionRefId);

      const index = visibleSections.findIndex(
        section => getDndSortableSectionId(section.ref) === over?.id
      );
      if (index >= 0) {
        virtuosoRef.current?.scrollToIndex({
          index,
          align: 'start',
          behavior: 'auto', // Smooth behavior is less performant, so please check for regressions if you change this value.
        });
      }
    },
    [moveSection, visibleSections, virtuosoRef]
  );

  return handleSectionDragEnd;
};

export const useHandlePanelDragEnd = ({
  panelDropRef,
}: {
  panelDropRef: React.RefObject<PanelDropRef | null>;
}) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const movePanel = useViewAction(panelBankConfigRef, Actions.movePanel);

  const handlePanelDragEnd = useCallback(
    (active: Active | null, over: Over | null) => {
      const activeData = active?.data.current;
      const targetData = over?.data.current;

      if (activeData?.panelRef && targetData) {
        movePanel(
          {
            panelRef: activeData.panelRef,
            sectionRef: activeData.sectionRef,
          },
          {
            panelRef: targetData.panelRef,
            sectionRef: targetData.sectionRef,
            insertDir: panelDropRef?.current?.insertDir || 'before',
          }
        );
      }
    },
    [panelDropRef, movePanel]
  );

  return handlePanelDragEnd;
};
